import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate, useRouter } from "@tanstack/react-router"
import {
  Bell,
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  LogOut,
  Menu,
  Palette,
  Trash2,
  UserRound,
  WandSparkles,
} from "lucide-react"
import { type ReactNode, useState } from "react"

import type {
  AppNotification,
  NotificationAction,
  NotificationCommandName,
} from "@/core/notifications/schemas"

import { Button } from "@/components/button"
import { GuildLogo } from "@/components/guild-branding"
import {
  ThemeSwitcher,
  ThemeSwitcherMenuItems,
} from "@/components/shared/theme-switcher"
import { useEffectiveIsAdmin, useViewAsMember } from "@/lib/admin-view-store"
import { authApi, type AuthUser } from "@/lib/auth-api"
import { AUTH_ME_QUERY_KEY } from "@/lib/auth-query"
import { memberDisplayLabel } from "@/lib/member-display-label"
import {
  useNotifications,
  useUnreadNotificationCount,
} from "@/lib/notifications-api"
import { profileApi } from "@/lib/profile-api"
import { OWN_PROFILE_ADMIN_ONLY } from "@/lib/profile-rollout"
import { notificationsKeys } from "@/lib/query-keys"
import {
  notificationStoreActions,
  useNotificationInboxCursor,
  useNotificationInboxCursorHistory,
  useNotificationInboxDialogOpen,
  useNotificationInboxFilter,
} from "@/lib/stores/notification-inbox-ui-store"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog"
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet"
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs"

interface AppHeaderProps {
  user: AuthUser
  mobileNavOpen: boolean
  onMobileNavOpenChange: (open: boolean) => void
  mobileNavContent: ReactNode
}

const formatNotificationTime = (value: string): string => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return "Unknown time"
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date)
}

const getNotificationPreview = (item: AppNotification) => {
  if (item.payload.kind === "gw.roster_published") {
    return {
      title: item.payload.title,
      body: `${item.payload.body} (${item.payload.data.rosterDate})`,
    }
  }

  if (item.payload.kind === "system.announcement") {
    return {
      title: item.payload.title,
      body: item.payload.body,
    }
  }

  return {
    title: "Notification",
    body: "You have a new update.",
  }
}

const parseUrlForNotification = (href: string): URL | null => {
  try {
    return new URL(href, window.location.origin)
  } catch {
    return null
  }
}

const getNotificationActionLabel = (action: NotificationAction): string => {
  if (action.type === "route") {
    return action.label?.trim() || "Open"
  }

  if (action.type === "open_url") {
    return action.label?.trim() || "Open link"
  }

  if (action.type === "command") {
    return action.label?.trim() || "Run"
  }

  return ""
}

export function AppHeader({
  user,
  mobileNavOpen,
  onMobileNavOpenChange,
  mobileNavContent,
}: AppHeaderProps) {
  const navigate = useNavigate()
  const router = useRouter()
  const queryClient = useQueryClient()
  const profileQuery = useQuery({
    queryKey: ["profile", user.userId] as const,
    queryFn: profileApi.get,
  })

  const viewAsMember = useViewAsMember()
  const effectiveIsAdmin = useEffectiveIsAdmin(user.isAdmin)

  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false)

  const {
    inboxQuery,
    markReadMutation,
    markAllReadMutation,
    deleteNotificationMutation,
    deleteReadNotificationsMutation,
  } = useNotifications()

  const unreadCount = useUnreadNotificationCount()
  const isNotificationsDialogOpen = useNotificationInboxDialogOpen()
  const notificationsCursor = useNotificationInboxCursor()
  const cursorHistory = useNotificationInboxCursorHistory()
  const notificationFilter = useNotificationInboxFilter()

  const commandHandlers: Record<NotificationCommandName, () => Promise<void>> =
    {
      refresh_notifications: async () => {
        await queryClient.invalidateQueries({
          queryKey: notificationsKeys.inboxBase(),
        })
      },
    }

  const runNotificationAction = async (action: NotificationAction) => {
    if (action.type === "none") {
      return
    }

    if (action.type === "route") {
      await navigate({
        to: action.to as never,
        params: action.params as never,
        search: action.search as never,
      })
      return
    }

    if (action.type === "open_url") {
      const targetUrl = parseUrlForNotification(action.href)
      if (!targetUrl || targetUrl.origin !== window.location.origin) {
        return
      }

      if (action.external) {
        window.open(targetUrl.toString(), "_blank", "noopener,noreferrer")
      } else {
        window.location.assign(targetUrl.toString())
      }
      return
    }

    const handler = commandHandlers[action.name]
    if (!handler) {
      return
    }

    await handler()
  }

  const userLabel = memberDisplayLabel({
    displayName: user.displayName,
    userId: user.userId,
  })
  const userInitials = userLabel
    .split(/\s+/)
    .filter((part) => part.length > 0)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")

  const handleLogout = async () => {
    try {
      await authApi.logout()
    } catch {}
    await queryClient.invalidateQueries({
      queryKey: AUTH_ME_QUERY_KEY,
    })
    await router.invalidate()
    await navigate({
      to: "/login",
      search: { redirect: undefined },
    })
  }

  const notifications = inboxQuery.data?.items ?? []
  const filteredNotifications =
    notificationFilter === "unread"
      ? notifications.filter((item) => item.readAt === null)
      : notifications
  const nextCursor = inboxQuery.data?.nextCursor ?? null

  return (
    <>
      <header className="glass-header sticky top-0 z-30 flex h-14 items-center border-b border-border/40 px-3 sm:px-4">
        <div className="flex items-center gap-2">
          <Sheet open={mobileNavOpen} onOpenChange={onMobileNavOpenChange}>
            <SheetTrigger
              render={
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-9 lg:hidden"
                />
              }
            >
              <Menu className="size-4" />
              <span className="sr-only">Open navigation</span>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-[min(20rem,92vw)] p-0"
              showCloseButton
            >
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <SheetDescription className="sr-only">
                Site navigation and quick links
              </SheetDescription>
              <div className="h-full overflow-y-auto p-2">
                {mobileNavContent}
              </div>
            </SheetContent>
          </Sheet>

          <GuildLogo />
        </div>

        <div className="flex flex-1 items-center justify-center">
          <div className="text-kicker text-sm text-foreground">Unicornis</div>
        </div>

        <div className="flex items-center gap-1.5">
          <Button
            size="icon"
            variant="ghost"
            className="relative size-9"
            onClick={() => {
              notificationStoreActions.setDialogOpen(true)
            }}
          >
            <Bell className="size-4" />
            <span className="sr-only">Notifications</span>
            {unreadCount > 0 && (
              <span
                aria-hidden="true"
                className="absolute -top-0.5 -right-0.5 flex min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[0.6rem] leading-none font-bold text-primary-foreground"
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </Button>

          <ThemeSwitcher className="hidden lg:flex" />
          <Button
            size="icon"
            variant="ghost"
            className="hidden size-9 lg:inline-flex"
            onClick={() => {
              void navigate({ to: "/theme-builder" })
            }}
          >
            <WandSparkles className="size-4" />
            <span className="sr-only">Open theme builder</span>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-9 sm:w-auto sm:gap-1.5 sm:px-3 lg:hidden"
                />
              }
            >
              <Palette className="size-4 sm:hidden" />
              <span className="sr-only sm:not-sr-only">Appearance</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 lg:hidden">
              <DropdownMenuGroup>
                <DropdownMenuLabel>Theme</DropdownMenuLabel>
                <ThemeSwitcherMenuItems />
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  void navigate({ to: "/theme-builder" })
                }}
              >
                <WandSparkles className="size-4" />
                Theme Builder
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-9 rounded-full"
                />
              }
            >
              <Avatar size="sm" className="bg-primary/10 text-primary">
                {profileQuery.data?.avatarUrl ? (
                  <AvatarImage
                    src={profileQuery.data.avatarUrl}
                    alt={userLabel}
                  />
                ) : null}
                <AvatarFallback>{userInitials || "U"}</AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuGroup>
                <DropdownMenuLabel>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-semibold text-foreground">
                      {userLabel}
                    </span>
                    <span className="text-kicker text-[0.68rem] tracking-[0.14em] text-muted-foreground">
                      {effectiveIsAdmin ? "Admin" : "Member"}
                      {user.isAdmin && viewAsMember && (
                        <span className="ml-1.5 text-info">
                          (viewing as member)
                        </span>
                      )}
                    </span>
                  </div>
                </DropdownMenuLabel>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={OWN_PROFILE_ADMIN_ONLY && !user.isAdmin}
                className="data-disabled:pointer-events-auto"
                title={
                  OWN_PROFILE_ADMIN_ONLY && !user.isAdmin
                    ? "Admin only"
                    : undefined
                }
                onClick={() => {
                  void navigate({ to: "/profile" })
                }}
              >
                <UserRound className="size-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onClick={() => {
                  setIsLogoutConfirmOpen(true)
                }}
              >
                <LogOut className="size-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <Dialog
        open={isNotificationsDialogOpen}
        onOpenChange={(open) => {
          notificationStoreActions.setDialogOpen(open)
        }}
      >
        <DialogContent className="max-w-[calc(100%-1rem)] p-0 sm:max-w-3xl">
          <DialogHeader className="border-b px-5 pt-5 pb-3">
            <DialogTitle className="flex items-center gap-2">
              <Bell className="size-4" />
              Notification Center
            </DialogTitle>
            <DialogDescription>
              Review updates, mark read, and manage your inbox.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 px-5 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-kicker text-xs font-semibold tracking-[0.12em] text-muted-foreground">
                Unread: {unreadCount}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  disabled={unreadCount === 0 || markAllReadMutation.isPending}
                  onClick={() => {
                    markAllReadMutation.mutate()
                  }}
                >
                  {markAllReadMutation.isPending ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : (
                    <Check className="size-3" />
                  )}
                  Mark all read
                </Button>
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  disabled={deleteReadNotificationsMutation.isPending}
                  onClick={() => {
                    deleteReadNotificationsMutation.mutate()
                  }}
                >
                  {deleteReadNotificationsMutation.isPending ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : (
                    <Trash2 className="size-3" />
                  )}
                  Remove all read
                </Button>
              </div>
            </div>

            <Tabs
              value={notificationFilter}
              onValueChange={(value) => {
                if (value === "all" || value === "unread") {
                  notificationStoreActions.setFilter(value)
                }
              }}
            >
              <TabsList className="h-7">
                <TabsTrigger value="all" className="px-2 py-0 text-xs">
                  All
                </TabsTrigger>
                <TabsTrigger value="unread" className="px-2 py-0 text-xs">
                  Unread
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {inboxQuery.isLoading ? (
              <div className="rounded-xl border border-border/60 px-3 py-6 text-center text-sm text-muted-foreground">
                Loading notifications...
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="rounded-xl border border-border/60 px-3 py-6 text-center text-sm text-muted-foreground">
                {notificationFilter === "unread"
                  ? "No unread notifications on this page."
                  : "You are all caught up."}
              </div>
            ) : (
              <div className="grid max-h-[60vh] gap-2 overflow-y-auto pr-1">
                {filteredNotifications.map((item) => {
                  const preview = getNotificationPreview(item)
                  const isClickable = item.payload.action.type !== "none"
                  const actionLabel = getNotificationActionLabel(
                    item.payload.action,
                  )
                  const isRead = Boolean(item.readAt)

                  return (
                    <div
                      key={item.id}
                      className={`rounded-xl border px-3 py-2.5 transition-all ${
                        isRead
                          ? "border-border/55 bg-card/35"
                          : "border-primary/45 bg-[color-mix(in_oklch,var(--primary)_12%,var(--card))] shadow-[0_10px_24px_-20px_color-mix(in_oklch,var(--primary)_95%,transparent)]"
                      } ${isClickable ? "hover:border-primary/35 hover:bg-card" : ""}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="truncate text-sm font-semibold text-foreground">
                              {preview.title}
                            </div>
                            {isRead ? (
                              <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-success/30 bg-success/10 px-1.5 py-0.5 text-[0.62rem] font-semibold text-success">
                                <Check className="size-3" />
                                Read
                              </span>
                            ) : (
                              <span className="inline-flex size-2 shrink-0 rounded-full bg-primary" />
                            )}
                          </div>
                          <div className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
                            {preview.body}
                          </div>
                          <div className="mt-1 text-[0.68rem] text-muted-foreground">
                            {formatNotificationTime(item.createdAt)}
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-1">
                          <Button
                            type="button"
                            size="icon-xs"
                            variant="ghost"
                            disabled={
                              Boolean(item.readAt) || markReadMutation.isPending
                            }
                            aria-label="Mark read"
                            title="Mark read"
                            onClick={() => {
                              markReadMutation.mutate(item.id)
                            }}
                          >
                            <Check className="size-3.5" />
                          </Button>

                          <Button
                            type="button"
                            size="icon-xs"
                            variant="ghost"
                            disabled={deleteNotificationMutation.isPending}
                            aria-label="Delete notification"
                            title="Delete notification"
                            onClick={() => {
                              deleteNotificationMutation.mutate(item.id)
                            }}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>

                          {isClickable && (
                            <Button
                              type="button"
                              size="xs"
                              variant="outline"
                              disabled={markReadMutation.isPending}
                              onClick={() => {
                                if (!item.readAt) {
                                  markReadMutation.mutate(item.id)
                                }
                                void runNotificationAction(item.payload.action)
                                notificationStoreActions.setDialogOpen(false)
                              }}
                            >
                              {item.payload.action.type === "open_url" ? (
                                <ExternalLink className="size-3" />
                              ) : (
                                <ChevronRight className="size-3" />
                              )}
                              {actionLabel}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            <div className="flex items-center justify-between border-t pt-3">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={cursorHistory.length === 0 || inboxQuery.isFetching}
                onClick={() => {
                  notificationStoreActions.goPreviousPage()
                }}
              >
                <ChevronLeft className="size-3.5" />
                Previous
              </Button>

              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={!nextCursor || inboxQuery.isFetching}
                onClick={() => {
                  if (!nextCursor) {
                    return
                  }

                  notificationStoreActions.goNextPage(nextCursor)
                }}
              >
                Next
                <ChevronRight className="size-3.5" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isLogoutConfirmOpen}
        onOpenChange={setIsLogoutConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Log out of Holy Unicorn?</AlertDialogTitle>
            <AlertDialogDescription>
              You will be returned to the login screen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                void handleLogout()
              }}
            >
              Log out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
